import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { parseVideoUrl, isVideoUrl, isGoogleUrl } from 'shared/utils';
import { ExternalLinkPreviewIcon, OpenNewWindow } from '../../../../images/ui';
import { Button, Loader } from '../../../core';
import { getLang } from 'shared/store/selectors/lang.selector';
import { EVENTS } from 'shared/constants';
import styles from './external-link-viewer.module.scss';
import classNames from 'classnames';
import knowledgeServiceApi from 'shared/api/knowledge.service.api';
import analyticsService from 'src/helpers/analytics.service';
import { openInNewTab as openInNewTabUtil } from '../../../../utils/external-link-provider';
import { buildIframeAllowRegex } from './iframe-allowlist';

const IFRAME_LOAD_TIMEOUT_MS = 10000;

// toolbar=0 hides the native PDF toolbar (download/print) for embedded previews
const PDF_VIEWER_PARAMS = '#toolbar=0';

const NotAvailableLink = ({ lang, link, openInNewTab }) => (
  <div className={styles.notAvailableLink}>
    <img src={ExternalLinkPreviewIcon} alt={''} className={styles.externalIcon} />
    <span className={styles.externalHeader}>{lang.LINK_NOT_AVAILABLE_HEADER}</span>
    <span className={styles.externalSubheader}>{lang.LINK_NOT_AVAILABLE_SUBHEADER}</span>
    <div>
      <Button size="small" buttonStyle="primary" onClick={openInNewTab} className={styles.button}>
        {link}
      </Button>
    </div>
  </div>
);

/**
 * @type {React.FC<any>}
 */

const ExternalLinkViewer = React.memo(({ id = '', title, link: initialLink }) => {
  const [linkIsLoading, setLinkIsLoading] = React.useState();
  const [iframeIsLoading, setIframeIsLoading] = React.useState(true);
  const [iframeLoadFailed, setIframeLoadFailed] = React.useState(false);
  const lang = useSelector(getLang('EXTERNAL_LINK_VIEWER'));
  const [imageAvailable, setImageAvailable] = useState(false);

  const [link, setLink] = useState();

  useEffect(() => {
    (async () => {
      if (!initialLink) {
        setLinkIsLoading(true);
        const res = await knowledgeServiceApi.getItemContentAsPDF({ id });
        setLink(res.data.url);
        setLinkIsLoading(false);
      } else {
        setLink(initialLink);
      }
    })();
  }, [id, link, initialLink]);

  // Timeout fallback for iframe loading - iframes don't fire onError for cross-origin failures
  useEffect(() => {
    if (!iframeIsLoading) return;

    const timeout = setTimeout(() => {
      if (iframeIsLoading) {
        setIframeIsLoading(false);
        setIframeLoadFailed(true);
      }
    }, IFRAME_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [iframeIsLoading]);

  const openInNewTab = React.useCallback(() => {
    openInNewTabUtil({ id, title, link });
    analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.OPEN_IN_NEW_TAB, {
      id,
      link,
      title,
    });
  }, [id, title, link]);

  const allowIframeDomain =
    useSelector(({ generalConfig }) => generalConfig.ALLOW_IFRAME_DOMAINS) || [];
  const allowIframeRegex = buildIframeAllowRegex(allowIframeDomain);

  if (linkIsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Loader />
      </div>
    );
  }

  if (!link) {
    return;
  }

  const videoEmbedUrl = isVideoUrl(link) ? parseVideoUrl(link) : '';

  // Only embed when we produced a real embed URL; otherwise fall through to the
  // allowlist / NotAvailableLink branches instead of rendering <iframe src="">.
  if (videoEmbedUrl) {
    return (
      <>
        <iframe
          className={styles.iframe}
          src={videoEmbedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
        <button type="button" className={styles.openExternal} onClick={openInNewTab}>
          {lang.OPEN_MEDIA}
          <img src={OpenNewWindow} alt={title} className={styles.openExternalIcon} />
        </button>
      </>
    );
  }

  if (allowIframeRegex.test(link) || isGoogleUrl(link)) {
    // Check if the image is available
    const image = new Image();
    image.src = link;
    image.onload = () => {
      setImageAvailable(true);
    };

    // Check if the link is a image file and render it accordingly
    if (/\.(jpe?g|png|gif)$/i.test(link) && imageAvailable) {
      return (
        <>
          <img className={styles.image} src={link} alt={title} loading="lazy" />
          <button type="button" className={styles.openExternal} onClick={openInNewTab}>
            {lang.OPEN_MEDIA}
            <img src={OpenNewWindow} alt={title} className={styles.openExternalIcon} />
          </button>
        </>
      );
    }

    const parsedLink = allowIframeRegex.test(link) ? `${link}${PDF_VIEWER_PARAMS}` : link;

    // If iframe failed to load, show the fallback UI
    if (iframeLoadFailed) {
      return <NotAvailableLink lang={lang} link={link} openInNewTab={openInNewTab} />;
    }

    return (
      <>
        <iframe
          className={classNames(styles.iframe, iframeIsLoading ? styles.hideIframe : null)}
          title={title}
          src={parsedLink}
          onLoad={() => setIframeIsLoading(false)}
        />
        {iframeIsLoading && (
          <div className={styles.loaderWrapper}>
            <Loader />
          </div>
        )}
      </>
    );
  }

  return <NotAvailableLink lang={lang} link={link} openInNewTab={openInNewTab} />;
});

export { ExternalLinkViewer };
